<template>
  <div class="spl-personal-settings__holder">
    <subpage-header back-link="dashboard" label="Personal Settings" />
    <message-modal />
    <div class="spl-personal-settings">
      <div class="spl-h3">
        Dashboard Settings
      </div>

      <span class="spl-text">Here  you can setup your default setting for the dashboard.</span>
      <div class="spl-personal-settings__elements">
        <div>
          <form-label>Default Date</form-label>
          <form-radiobutton-group
            :options="[{label: 'Last working day', value: -1}, {label: 'Today', value: 0}]"
            :value="defaultDate"
            :horizontal="true"
            :toggle="false"
            @radiobuttonChanged="default_date_changed" />
        </div>
        <div>
          <form-label>Default Grouping</form-label>
          <form-radiobutton-group
            :options="[{label: 'Customer', value: 'Customer'}, {label: 'Country', value: 'Country'}]"
            :value="defaultGrouping"
            :horizontal="true"
            :toggle="false"
            @radiobuttonChanged="default_grouping_changed" />
        </div>
      </div>
      <div class="spl-personal-settings__elements">
        <div>
          <!-- *********************** Country  *********************************** -->
          <form-label>Default Dashboard Countries</form-label>
          <country-multiselect
            :selected-keys="defaultCountries"
            @valuesSelected="onCountriesSelected" />
        </div>
        <div>
          <!-- *********************** Customer  *********************************** -->
          <form-label>Default Dashboard Customers</form-label>
          <customer-multiselect
            :selected-keys="defaultCustomers"
            @valuesSelected="onCustomersSelected" />
        </div>

        <div>
          <!-- *********************** Parcel Status  *********************************** -->
          <form-label>Default Parcel Status</form-label>
          <form-filtered-multiselect
            :value="defaultParcelStatus"
            :options="constants.PARCEL_STATUS_OPTIONS"
            track-by="value"
            label="label"
            placeholder="Parcel Status"
            @input="onParcelStatusSelected" />
        </div>
        <div>
          <!-- *********************** Service Levels  *********************************** -->
          <form-label>Default Service Levels</form-label>
          <form-filtered-multiselect
            :value="defaultServiceLevels"
            :options="serviceLevelOptions"
            track-by="value"
            label="label"
            placeholder="Service Level"
            @input="onServiceLevelSelected" />
        </div>

        <div>
          <!-- *********************** Events  *********************************** -->
          <form-label>Default Current Events</form-label>
          <form-filtered-multiselect
            :value="defaultEvents"
            :options="eventInfoOptions"
            track-by="value"
            label="label"
            placeholder="Current Event"
            @input="onEventsSelected" />
        </div>
        <!-- *********************** Providers  *********************************** -->
        <div v-if="userHasPermission(constants.PERMISSION_TYPES.PROVIDER, constants.PERMISSION_VALUES.LIST)">
          <form-label>Default Provider</form-label>
          <provider-multiselect
            :selected-keys="defaultProviders"
            @valuesSelected="onProvidersSelected" />
        </div>

        <!-- *********************** Tags  *********************************** -->
        <div v-if="userHasPermission(constants.PERMISSION_TYPES.TAG, constants.PERMISSION_VALUES.LIST)">
          <form-label>Default Tag</form-label>
          <tag-multiselect
            :selected-keys="defaultTags"
            @valuesSelected="onTagsSelected" />
        </div>

      </div>
      <div class="spl-personal-settings__elements">

        <div class="spl-parcel-filter__other--postal-from">
          <form-label>Postal Code From</form-label>
          <form-input
            :value="defaultPostalCodeFrom"
            @input="onDefaultPostalCodeFromChanged" />
        </div>
        <div class="spl-parcel-filter__other--postal-to">
          <form-label>Postal Code To</form-label>
          <form-input
            :value="defaultPostalCodeTo"
            @input="onDefaultPostalCodeToChanged" />
        </div>
      </div>
      <div class="spl-personal-settings__elements">
        <div class="spl-parcel-filter__flow">
          <form-label>Show Flow</form-label>
          <form-toggle-switch
            :value="defaultFlow === 'Forward'"
            :pre-selected="true"
            text-unchecked="Return"
            text-checked="Forward"
            @input="onDefaultFlowSelected" />
        </div>
      </div>

      <div class="spl-personal-settings__buttons">
        <form-button
          :wide="true"
          :secondary="true"
          @onClick="cancel">
          Cancel
        </form-button>
        <form-button :wide="true" @onClick="handleSaveClicked">
          Save
        </form-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import constants from '@/constants'

export default {
  name: '',
  data () {
    return {
      constants
    }
  },
  computed: {
    ...mapGetters('personalSettings', ['defaultCountries',
      'defaultCustomers',
      'defaultProviders',
      'defaultTags',
      'defaultDate',
      'defaultGrouping',
      'defaultParcelStatus',
      'defaultServiceLevels',
      'defaultEvents',
      'defaultPostalCodeFrom',
      'defaultPostalCodeTo',
      'defaultFlow']),
    ...mapGetters('serviceLevel', ['serviceLevelOptions']),
    ...mapState('eventInfo', ['eventInfoOptions'])
  },
  mounted () {
    this.cacheSettings()
  },
  methods: {
    ...mapActions('personalSettings', ['savePersonalSettings']),
    ...mapActions('parcelFilter', ['initRootState']),
    ...mapMutations('personalSettings', ['setDefaultCountries',
      'setDefaultCustomers',
      'setDefaultProviders',
      'setDefaultTags',
      'setDefaultGrouping',
      'setDefaultParcelStatus',
      'setDefaultServiceLevels',
      'setDefaultPostalCodeFrom',
      'setDefaultPostalCodeTo',
      'setDefaultEvents',
      'setDefaultDate',
      'setDefaultFlow',
      'cacheSettings',
      'resetSettingsFromCache']),
    /// Load the options for the user permissions (Customer / Country)
    handleSaveClicked () {
      this.savePersonalSettings().then(() => {
        this.initRootState()
        this.$router.push('../dashboard')
      }).catch((error) => {
        window.bus.$emit('openErrorModal', error.ExceptionMessage)
      })
    },
    // The user changed is mind, no new user should be created => back to dashboard
    cancel () {
      this.resetSettingsFromCache()
      this.$router.push('../dashboard')
    },
    onCustomersSelected (customers) {
      this.setDefaultCustomers(customers)
    },
    onProvidersSelected (providers) {
      this.setDefaultProviders(providers)
    },
    onTagsSelected (tags) {
      this.setDefaultTags(tags)
    },
    onCountriesSelected (countries) {
      this.setDefaultCountries(countries)
    },
    onParcelStatusSelected (status) {
      this.setDefaultParcelStatus(status)
    },
    onServiceLevelSelected (serviceLevels) {
      this.setDefaultServiceLevels(serviceLevels)
    },
    onEventsSelected (events) {
      this.setDefaultEvents(events)
    },
    onDefaultPostalCodeFromChanged (postalCodeFrom) {
      this.setDefaultPostalCodeFrom(postalCodeFrom)
    },
    onDefaultPostalCodeToChanged (postalCodeTo) {
      this.setDefaultPostalCodeTo(postalCodeTo)
    },
    onDefaultFlowSelected (flow) {
      this.setDefaultFlow(flow ? 'Forward' : 'Return')
    },
    // The default date has changed
    default_date_changed (newValue) {
      this.setDefaultDate(newValue)
    },
    // The default grouping has changed
    default_grouping_changed (newValue) {
      this.setDefaultGrouping(newValue)
    }
  }
}
</script>

<style lang="scss">
  @import '../variables.scss';

.spl-personal-settings {
  margin: 0 70px;
  &__holder {
    min-height: 100vh;
    background-color: $tableRowBackgroundColorSelected;
  }
  & .spl-h3 {
    margin-top: 20px;
     @include mobile() {
       margin-top: 100px;
     }
  }

  &__elements {
    margin-top: 20px;
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    & > div {
      min-width: 300px;
      max-width: 300px;
      margin-right: 30px;
      margin-top: 30px;
    }
  }

  &__buttons {
    display: flex;
    flex-wrap: wrap;
    & > button {
      max-width: 135px;
      margin-right: 30px;
    }
  }
}
</style>
